
import { useState } from 'react';
import { Separator } from '@/components/ui/separator';
import { useIsMobile } from '@/hooks/use-mobile';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ReferralDashboard } from '@/components/affiliates/ReferralDashboard';
import { ReferralTools } from '@/components/affiliates/ReferralTools';
import { usePermissions } from '@/contexts/unified-permissions';
import { OfflineWrapper } from '@/contexts/offline/components/OfflineUIComponents';
import { CircleHelp } from 'lucide-react';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';

export default function Affiliates() {
  const isMobile = useIsMobile();
  const { user } = usePermissions();
  const [helpOpen, setHelpOpen] = useState(false);
  
  const helpContent = {
    title: "Referral Program Help",
    description: "Share The Mat Monster and earn rewards",
    content: (
      <>
        <p>Welcome to our Referral Program! Here you can:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Generate unique referral links to share with others</li>
          <li>Track the performance of your referrals</li>
          <li>See your rewards and earnings</li>
          <li>Access sharing tools to promote The Mat Monster</li>
        </ul>
        <p>The more people you refer who sign up, the more rewards you earn!</p>
      </>
    )
  };
  
  return (
    <OfflineWrapper>
      <div className="space-y-6 max-w-3xl mx-auto p-4">

        {!isMobile && (
          <>
            <div>
              <h1 className="text-2xl font-bold tracking-tight inline-flex items-center gap-2">
                Referral Program
                <CircleHelp
                  className="h-5 w-5 text-primary cursor-pointer hover:text-primary/80 transition-colors"
                  onClick={() => setHelpOpen(true)}
                  strokeWidth={2}
                />
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Share The Mat Monster with friends and earn rewards.
              </p>
            </div>
            <Separator />
          </>
        )}

        <Tabs defaultValue="dashboard" className="w-full">
          <TabsList className={`grid w-full ${isMobile ? 'grid-cols-1 gap-2' : 'grid-cols-2'}`}>
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="tools">Sharing Tools</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-6">
            <ReferralDashboard />
          </TabsContent>

          <TabsContent value="tools" className="mt-6">
            <ReferralTools userId={user?.id} />
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Help Dialog */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{helpContent.title}</DialogTitle>
            <DialogDescription>
              {helpContent.description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {helpContent.content}
          </div>
          <div className="flex justify-end">
            <DialogClose asChild>
              <Button variant="default" size="sm">Got it</Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </OfflineWrapper>
  );
}
